import React, { useMemo, useState } from "react";
import {
  buildDocumentLearningStats,
  filterLearningStaffByStatus,
  getStaffLearningStatusMeta,
} from "./qmsDocumentLibraryLearningUtils";
import { formatReviewDateCell } from "./qmsReviewDateUtils";

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Done" },
];

function QmsDocumentLibraryLearningPanel({
  documentRow,
  staff = [],
  loading = false,
  remindingStaffIds = [],
  canSendReminders = false,
  onSendReminder,
  onSendBulkReminder,
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const review = useMemo(
    () => formatReviewDateCell(documentRow || {}),
    [documentRow],
  );

  const stats = useMemo(
    () => buildDocumentLearningStats(staff, documentRow),
    [staff, documentRow],
  );

  const remindingSet = useMemo(
    () => new Set((remindingStaffIds || []).map(String)),
    [remindingStaffIds],
  );

  const filteredStaff = useMemo(() => {
    const byStatus = filterLearningStaffByStatus(staff, statusFilter, documentRow);
    const q = search.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((row) => {
      const name = String(row.StaffName || "").toLowerCase();
      const role = String(row.Roles || "").toLowerCase();
      return name.includes(q) || role.includes(q);
    });
  }, [staff, statusFilter, documentRow, search]);

  const remindablePending = useMemo(
    () => stats.pending.filter((row) => !remindingSet.has(String(row.StaffId))),
    [stats.pending, remindingSet],
  );

  const filterCounts = useMemo(
    () => ({
      all: stats.total,
      pending: stats.pendingCount,
      overdue: stats.overdueCount,
      completed: stats.completedCount,
    }),
    [stats],
  );

  const visibleRemindable = useMemo(
    () =>
      filteredStaff.filter((row) => {
        const key = getStaffLearningStatusMeta(row, documentRow).key;
        return (
          (key === "pending" || key === "overdue") &&
          !remindingSet.has(String(row.StaffId))
        );
      }),
    [documentRow, filteredStaff, remindingSet],
  );

  const selectedRemindable = useMemo(
    () =>
      filteredStaff.filter((row) => {
        const key = getStaffLearningStatusMeta(row, documentRow).key;
        return (
          selectedIds.has(String(row.StaffId)) &&
          (key === "pending" || key === "overdue") &&
          !remindingSet.has(String(row.StaffId))
        );
      }),
    [documentRow, filteredStaff, remindingSet, selectedIds],
  );

  const allVisibleSelected =
    visibleRemindable.length > 0 &&
    visibleRemindable.every((row) => selectedIds.has(String(row.StaffId)));

  const toggleSelected = (staffId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(staffId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(visibleRemindable.map((row) => String(row.StaffId))));
  };

  const handleBulkRemind = () => {
    if (!selectedRemindable.length) return;
    onSendBulkReminder?.(selectedRemindable);
    setSelectedIds(new Set());
  };

  return (
    <div className="qms-doc-workspace__panel qms-doc-lib-learning">
      <div className="qms-doc-lib-learning__top">
        <div className="qms-doc-lib-learning__top-main">
          <div className="qms-doc-lib-learning__title-row">
            <h6 className="qms-doc-lib-learning__title">Learning progress</h6>
            {review.badge ? (
              <span
                className={`qms-doc-lib-learning__due-chip is-${review.status || "none"}`}
              >
                {review.badge}
              </span>
            ) : null}
          </div>
          <div className="qms-doc-lib-learning__progress-line">
            <div
              className="qms-doc-lib-learning__progress-track"
              style={{ "--qms-learning-progress": `${stats.completionPct}%` }}
            >
              <span className="qms-doc-lib-learning__progress-fill" />
            </div>
            <span className="qms-doc-lib-learning__progress-label">
              {stats.completedCount}/{stats.total} · {stats.completionPct}%
            </span>
          </div>
          <div className="qms-doc-lib-learning__chips">
            <span className="qms-doc-lib-learning__chip is-success">
              {stats.completedCount} done
            </span>
            <span className="qms-doc-lib-learning__chip is-warning">
              {stats.pendingCount} pending
            </span>
            <span className="qms-doc-lib-learning__chip is-danger">
              {stats.overdueCount} overdue
            </span>
            <span className="qms-doc-lib-learning__chip is-neutral">
              {stats.total} assigned
            </span>
          </div>
        </div>

        {canSendReminders ? (
          <div className="qms-doc-lib-learning__top-actions">
            <button
              type="button"
              className="qms-doc-lib-learning__icon-btn"
              title="Remind all pending"
              disabled={!remindablePending.length || remindingSet.size > 0}
              onClick={() => onSendBulkReminder?.(remindablePending)}
            >
              <i className="fas fa-bell" aria-hidden="true" />
              <span>All pending</span>
            </button>
            <button
              type="button"
              className="qms-doc-lib-learning__icon-btn is-primary"
              title="Remind selected staff"
              disabled={!selectedRemindable.length}
              onClick={handleBulkRemind}
            >
              <i className="fas fa-paper-plane" aria-hidden="true" />
              <span>Selected ({selectedRemindable.length})</span>
            </button>
          </div>
        ) : null}
      </div>

      <div className="qms-doc-lib-learning__controls">
        <div
          className="qms-doc-lib-learning__filters"
          role="tablist"
          aria-label="Filter by status"
        >
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              role="tab"
              aria-selected={statusFilter === filter.key}
              className={`qms-doc-lib-learning__filter-btn${
                statusFilter === filter.key ? " is-active" : ""
              }`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="qms-doc-lib-learning__filter-count">
                {filterCounts[filter.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <label className="qms-doc-lib-learning__search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Search name or role…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      <div className="qms-doc-lib-learning__table-wrap">
        {loading ? (
          <div className="qms-doc-lib-learning__empty">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        ) : !stats.total ? (
          <div className="qms-doc-lib-learning__empty">
            <p className="mb-0">No My Learning staff assigned yet.</p>
          </div>
        ) : !filteredStaff.length ? (
          <div className="qms-doc-lib-learning__empty">
            <p className="mb-0">No staff match this filter.</p>
          </div>
        ) : (
          <table className="qms-doc-lib-learning__table">
            <thead>
              <tr>
                {canSendReminders ? (
                  <th className="is-check">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={!visibleRemindable.length}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible pending staff"
                    />
                  </th>
                ) : null}
                <th>Staff</th>
                <th className="is-role">Role</th>
                <th className="is-status">Status</th>
                {canSendReminders ? <th className="is-action" /> : null}
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map((row) => {
                const statusMeta = getStaffLearningStatusMeta(row, documentRow);
                const staffId = String(row.StaffId || "");
                const isReminding = remindingSet.has(staffId);
                const canRemind =
                  canSendReminders &&
                  (statusMeta.key === "pending" || statusMeta.key === "overdue");

                return (
                  <tr key={staffId} className={`is-${statusMeta.tone}`}>
                    {canSendReminders ? (
                      <td className="is-check">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(staffId)}
                          disabled={!canRemind || isReminding}
                          onChange={() => toggleSelected(staffId)}
                          aria-label={`Select ${row.StaffName || "staff"}`}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="qms-doc-lib-learning__staff-cell">
                        <span className="qms-doc-lib-learning__avatar" aria-hidden="true">
                          {getInitials(row.StaffName)}
                        </span>
                        <span className="qms-doc-lib-learning__name">
                          {row.StaffName || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="is-role">{row.Roles || "—"}</td>
                    <td className="is-status">
                      <span
                        className={`qms-doc-lib-learning__status is-${statusMeta.tone}`}
                      >
                        {statusMeta.label}
                      </span>
                    </td>
                    {canSendReminders ? (
                      <td className="is-action">
                        {canRemind ? (
                          <button
                            type="button"
                            className="qms-doc-lib-learning__remind-btn"
                            disabled={isReminding}
                            title={`Remind ${row.StaffName || "staff"}`}
                            onClick={() => onSendReminder?.(row)}
                          >
                            {isReminding ? (
                              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                            ) : (
                              <i className="fas fa-bell" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          <span className="qms-doc-lib-learning__action-placeholder">
                            —
                          </span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default QmsDocumentLibraryLearningPanel;
